import React, { useEffect, useState } from "react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { AlertCircle, Calendar as CalendarIcon, Users, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import api, { getActiveStudio } from "@/lib/api";

const InstructorDashboard = ({ user, onLogout }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const studioAssignments = user?.studio_assignments || [];
  const noStudio = studioAssignments.length === 0;
  const activeStudio = getActiveStudio();

  useEffect(() => {
    if (noStudio) { setLoading(false); return; }
    api.get("/batches")
      .then((r) => setBatches(r.data.batches || []))
      .catch(() => setBatches([]))
      .finally(() => setLoading(false));
  }, [noStudio]);

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />

      <div className="p-4 md:p-8 pb-20 md:pb-8 max-w-5xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900" data-testid="instructor-dashboard-title">
          Welcome, {user?.name || "Instructor"}
        </h1>
        <p className="text-slate-600 mt-2">
          {activeStudio ? `Active studio: ${activeStudio.name}` : "Manage your classes and mark attendance"}
        </p>

        {noStudio && (
          <div data-testid="no-studio-banner" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900">No studio assigned</p>
              <p className="text-sm text-amber-800 mt-1">
                You don't have any studios assigned to your account yet. Please contact your studio admin to be added before you can manage classes, students, and payments.
              </p>
            </div>
          </div>
        )}

        {!noStudio && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              <Link to="/calendar" data-testid="instr-tile-calendar" className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 transition">
                <CalendarIcon className="w-6 h-6 text-violet-600" />
                <div className="text-2xl font-bold mt-3 text-slate-900">View Calendar</div>
                <div className="text-sm text-slate-500 mt-1">See today's classes</div>
              </Link>
              <Link to="/attendance" data-testid="instr-tile-attendance" className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 transition">
                <Users className="w-6 h-6 text-pink-600" />
                <div className="text-2xl font-bold mt-3 text-slate-900">Mark Attendance</div>
                <div className="text-sm text-slate-500 mt-1">Mark today's session</div>
              </Link>
              <Link to="/payments" data-testid="instr-tile-payments" className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 transition">
                <DollarSign className="w-6 h-6 text-emerald-600" />
                <div className="text-2xl font-bold mt-3 text-slate-900">Record Payments</div>
                <div className="text-sm text-slate-500 mt-1">Collect from students</div>
              </Link>
            </div>

            <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-5">
              <h2 className="text-base font-semibold text-slate-900">Your Batches</h2>
              {loading && <p className="text-sm text-slate-500 mt-3">Loading...</p>}
              {!loading && batches.length === 0 && (
                <p className="text-sm text-slate-500 mt-3">No batches assigned to you yet.</p>
              )}
              <div className="mt-3 space-y-2">
                {batches.map((b) => (
                  <div key={b.batch_id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{b.name}</p>
                      <p className="text-xs text-slate-500">{b.level} · {(b.schedule || []).length} sessions/wk</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <BottomNav user={user} onLogout={onLogout} />
    </div>
  );
};

export default InstructorDashboard;
